import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, CheckCircle, XCircle, Coffee } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";

const statusConfig = {
  present: { label: "Mevcut", icon: CheckCircle, color: "bg-emerald-400/10 text-emerald-400" },
  absent: { label: "Yok", icon: XCircle, color: "bg-rose-400/10 text-rose-400" },
  half_day: { label: "Yarım Gün", icon: Coffee, color: "bg-amber-400/10 text-amber-400" },
  remote: { label: "Uzaktan", icon: Clock, color: "bg-sky-400/10 text-sky-400" }
};

const Attendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    date: "",
    status: "present",
    check_in: "",
    check_out: "",
    overtime_hours: 0,
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedEmployee]);

  const fetchData = async () => {
    try {
      let url = `${API}/attendance?month=${selectedMonth}`;
      if (selectedEmployee !== "all") {
        url += `&employee_id=${selectedEmployee}`;
      }
      
      const [attendanceRes, employeesRes] = await Promise.all([
        axios.get(url, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true })
      ]);
      setAttendance(attendanceRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        overtime_hours: parseFloat(formData.overtime_hours) || 0
      };
      
      if (editingAttendance) {
        await axios.put(`${API}/attendance/${editingAttendance.id}`, data, { withCredentials: true });
        toast.success("Puantaj güncellendi");
      } else {
        await axios.post(`${API}/attendance`, data, { withCredentials: true });
        toast.success("Puantaj eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "İşlem başarısız";
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu puantaj kaydını silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/attendance/${id}`, { withCredentials: true });
      toast.success("Puantaj silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const openEditDialog = (att) => {
    setEditingAttendance(att);
    setFormData({
      employee_id: att.employee_id,
      date: att.date,
      status: att.status,
      check_in: att.check_in || "",
      check_out: att.check_out || "",
      overtime_hours: att.overtime_hours || 0,
      notes: att.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAttendance(null);
    setFormData({
      employee_id: selectedEmployee !== "all" ? selectedEmployee : "",
      date: new Date().toISOString().split('T')[0],
      status: "present",
      check_in: "",
      check_out: "",
      overtime_hours: 0,
      notes: ""
    });
  };

  // Calculate stats
  const stats = {
    present: attendance.filter(a => a.status === 'present').length,
    absent: attendance.filter(a => a.status === 'absent').length,
    half_day: attendance.filter(a => a.status === 'half_day').length,
    remote: attendance.filter(a => a.status === 'remote').length,
    total_overtime: attendance.reduce((sum, a) => sum + (a.overtime_hours || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Puantaj</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Mevcut: {stats.present} | Yok: {stats.absent} | Yarım: {stats.half_day} | Uzaktan: {stats.remote}
          </p>
        </div>
        <div className="flex gap-2">
          <Input 
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-40 bg-zinc-900 border-zinc-800"
            data-testid="attendance-month-picker"
          />
          <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
            <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800" data-testid="attendance-employee-filter">
              <SelectValue placeholder="Personel" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">Tüm Personel</SelectItem>
              {employees.map(e => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
            data-testid="add-attendance-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Kayıt Ekle
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(statusConfig).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <div key={key} className="bg-zinc-900 border border-zinc-800 p-4 rounded-sm">
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-sm ${config.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">{config.label}</p>
                  <p className="text-xl font-mono text-zinc-50">{stats[key]}</p>
                </div>
              </div>
            </div>
          );
        })}
        <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-sm">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-sm bg-amber-400/10 text-amber-400">
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs text-zinc-500">Mesai</p>
              <p className="text-xl font-mono text-zinc-50">{stats.total_overtime}s</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Personel</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tarih</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Durum</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Giriş</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Çıkış</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Mesai</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {attendance.map((att, index) => {
                const config = statusConfig[att.status] || statusConfig.present;
                const Icon = config.icon;
                return (
                  <motion.tr 
                    key={att.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors"
                    data-testid={`attendance-row-${att.id}`}
                  >
                    <td className="px-4 py-4">
                      <p className="text-sm text-zinc-50">{att.employee_name}</p>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-400">
                      {att.date}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs ${config.color}`}>
                          <Icon className="w-3 h-3" />
                          {config.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center text-sm font-mono text-zinc-400">
                      {att.check_in || "-"}
                    </td>
                    <td className="px-4 py-4 text-center text-sm font-mono text-zinc-400">
                      {att.check_out || "-"}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {att.overtime_hours > 0 ? (
                        <span className="text-sm font-mono text-amber-400">+{att.overtime_hours}s</span>
                      ) : (
                        <span className="text-sm text-zinc-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEditDialog(att)}
                          className="hover:bg-zinc-800 text-zinc-400 hover:text-zinc-50"
                          data-testid={`edit-attendance-${att.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(att.id)}
                          className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                          data-testid={`delete-attendance-${att.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
              {attendance.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">
                    Bu dönem için puantaj kaydı eklenmemiş
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingAttendance ? "Puantaj Düzenle" : "Yeni Puantaj Kaydı"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Personel *</Label>
                <Select 
                  value={formData.employee_id} 
                  onValueChange={(v) => setFormData({ ...formData, employee_id: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="attendance-employee-select">
                    <SelectValue placeholder="Personel seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Tarih *</Label>
                <Input 
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="attendance-date-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Durum *</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="attendance-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Giriş Saati</Label>
                <Input 
                  type="time"
                  value={formData.check_in}
                  onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="attendance-checkin-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Çıkış Saati</Label>
                <Input 
                  type="time"
                  value={formData.check_out}
                  onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="attendance-checkout-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Fazla Mesai (saat)</Label>
                <Input 
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.overtime_hours}
                  onChange={(e) => setFormData({ ...formData, overtime_hours: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="attendance-overtime-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Notlar</Label>
                <Input 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="attendance-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-attendance-btn"
              >
                {editingAttendance ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;
